import type {
  BaseCurrentCartServiceProps,
  Cart,
  CartV1,
} from '../current-cart-service/types';
import { SecondaryEvents } from '../../constants';
import { BaseCurrentCartService } from '../current-cart-service/BaseCurrentCartService';
import { CurrentCartService } from '../current-cart-service/CurrentCartService';
import { serialize, deserialize } from '../../utils/serialize';
import { currentCartV2 as currentCart } from '@wix/ecom';

export class SecondaryCurrentCartService extends BaseCurrentCartService {
  public waitForPrimaryReady?: Promise<void>;

  constructor(props: BaseCurrentCartServiceProps) {
    super(props);
    this.waitForPrimaryReady = this.waitForPrimaryOrInit(props);
  }

  private waitForPrimaryOrInit(props: BaseCurrentCartServiceProps) {
    // eslint-disable-next-line prefer-const
    let ackEventSubscribeId: number | undefined;
    let resolve: () => void;
    const promise = new Promise<void>((r) => (resolve = r));

    const timeoutId = setTimeout(() => {
      ackEventSubscribeId &&
        this.apis.pubSub.unsubscribe(SecondaryEvents.ACK, ackEventSubscribeId);
      const primaryCurrentCartService = new CurrentCartService(props);
      primaryCurrentCartService.init();
      resolve();
    }, 200);

    ackEventSubscribeId = this.apis.pubSub.subscribe(
      SecondaryEvents.ACK,
      () => {
        clearTimeout(timeoutId);
        this.apis.pubSub.unsubscribe(SecondaryEvents.ACK, ackEventSubscribeId!);
        resolve();
      },
      false,
    );

    this.apis.pubSub.publish(SecondaryEvents.HEALTH_CHECK, {}, false);

    return promise;
  }

  private promiseHandlers: {
    [a: string]: {
      resolve: (payload: any) => void;
      reject: (error: any) => void;
    };
  } = {};

  public init(): void {
    this.listenOnCacheChanges();
  }

  public getCurrentCart(): Promise<Cart> {
    return this.performActionOnMainCurrentCartService<Cart>(
      SecondaryEvents.GetRequest,
    );
  }

  public getCurrentCartV1(): Promise<CartV1> {
    return this.performActionOnMainCurrentCartService<CartV1>(
      SecondaryEvents.GetV1Request,
    );
  }

  public calculateCurrentCart(
    options?: currentCart.CalculateCurrentCartRequest,
  ): Promise<currentCart.CalculateCurrentCartResponse> {
    return this.performActionOnMainCurrentCartService<currentCart.CalculateCurrentCartResponse>(
      SecondaryEvents.CalculateCurrentCart,
      [options],
    );
  }

  public estimateCurrentCart(
    options?: currentCart.EstimateCurrentCartRequest,
  ): Promise<currentCart.EstimateCurrentCartResponse> {
    return this.performActionOnMainCurrentCartService<currentCart.EstimateCurrentCartResponse>(
      SecondaryEvents.EstimateCurrentCart,
      [options],
    );
  }

  public getUpsellOptionsForCurrentCart(): Promise<currentCart.GetUpsellOptionsForCurrentCartResponse> {
    return this.performActionOnMainCurrentCartService<currentCart.GetUpsellOptionsForCurrentCartResponse>(
      SecondaryEvents.GetUpsellOptionsForCurrentCart,
    );
  }

  public invalidateAndReload() {
    return this.performActionOnMainCurrentCartService<void>(
      SecondaryEvents.RefreshV1Request,
    );
  }

  public updateLineItemsInCurrentCart(
    options: currentCart.UpdateLineItemsInCurrentCartOptions,
  ) {
    return this.performActionOnMainCurrentCartService<Cart>(
      SecondaryEvents.UpdateLineItemsInCurrentCartRequest,
      [options],
    );
  }

  public removeLineItemsFromCurrentCart(options: { lineItemIds: string[] }) {
    return this.performActionOnMainCurrentCartService<Cart>(
      SecondaryEvents.RemoveLineItemsFromCurrentCartRequest,
      [options],
    );
  }

  public onLoginStart() {
    return this.performActionOnMainCurrentCartService<void>(
      SecondaryEvents.OnLoginStartRequest,
    );
  }

  public onLoginFinish() {
    return this.performActionOnMainCurrentCartService<void>(
      SecondaryEvents.OnLoginFinishRequest,
    );
  }

  public addCouponToCurrentCart(options: { coupon: currentCart.CouponInput }) {
    return this.performActionOnMainCurrentCartService<Cart>(
      SecondaryEvents.AddCouponToCurrentCartRequest,
      [options],
    );
  }

  public removeCouponFromCurrentCart(options: { couponId: string }) {
    return this.performActionOnMainCurrentCartService<Cart>(
      SecondaryEvents.RemoveCouponFromCurrentCartRequest,
      [options],
    );
  }

  public setDeliveryMethodForCurrentCart(options: {
    deliveryMethod: currentCart.DeliveryMethodInput;
  }) {
    return this.performActionOnMainCurrentCartService<Cart>(
      SecondaryEvents.SetDeliveryMethodForCurrentCartRequest,
      [options],
    );
  }

  public removeDeliveryMethodFromCurrentCart(): Promise<Cart> {
    return this.performActionOnMainCurrentCartService<Cart>(
      SecondaryEvents.RemoveDeliveryMethodFromCurrentCartRequest,
    );
  }

  public addLineItemsToCurrentCart(
    options: currentCart.AddLineItemsToCurrentCartOptions,
  ): Promise<Cart> {
    return this.performActionOnMainCurrentCartService<Cart>(
      SecondaryEvents.AddLineItemsToCurrentCart,
      [options],
    );
  }

  public updateCurrentCart(cart: Cart): Promise<Cart> {
    return this.performActionOnMainCurrentCartService<Cart>(
      SecondaryEvents.UpdateCurrentCartRequest,
      [cart],
    );
  }

  public refreshCurrentCart(): Promise<Cart> {
    return this.performActionOnMainCurrentCartService<Cart>(
      SecondaryEvents.RefreshCurrentCartRequest,
    );
  }

  public removeGiftCardFromCurrentCart(options: {
    giftCardId: string;
  }): Promise<Cart> {
    return this.performActionOnMainCurrentCartService<Cart>(
      SecondaryEvents.RemoveGiftCardFromCurrentCartRequest,
      [options],
    );
  }

  public addGiftCardToCurrentCart(options: {
    giftCardInput: currentCart.GiftCardInput;
  }): Promise<Cart> {
    return this.performActionOnMainCurrentCartService<Cart>(
      SecondaryEvents.AddGiftCardToCurrentCartRequest,
      [options],
    );
  }

  public updateFormSubmissionsInCurrentCart(
    options: currentCart.UpdateFormSubmissionsInCurrentCartOptions,
  ): Promise<Cart> {
    return this.performActionOnMainCurrentCartService<Cart>(
      SecondaryEvents.UpdateFormSubmissionsInCurrentCartRequest,
      [options],
    );
  }

  private listenOnCacheChanges() {
    this.apis.pubSub.subscribe(
      SecondaryEvents.ActionResponse,
      ({ data }) => {
        const { payload, key, error } = deserialize(data);
        /* istanbul ignore else */
        if (error) {
          this.promiseHandlers[key]?.reject(error);
        } else {
          this.promiseHandlers[key]?.resolve(payload);
        }

        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete this.promiseHandlers[key];
      },
      false,
    );

    this.apis.pubSub.subscribe(
      SecondaryEvents.Updated,
      () => {
        this.runCallbacks();
      },
      false,
    );
  }

  public getCurrentCartIdFromCache(): Promise<string | undefined> {
    return this.performActionOnMainCurrentCartService<string | undefined>(
      SecondaryEvents.GetCurrentCartIdFromCacheRequest,
    );
  }

  private async performActionOnMainCurrentCartService<T>(
    event: string,
    payload: any = undefined,
  ): Promise<T> {
    await this.waitForPrimaryReady;
    const key = generateRequestKey(event);
    const promise = new Promise<T>(
      (resolve, reject) => (this.promiseHandlers[key] = { resolve, reject }),
    );

    const data = serialize({ key, payload });
    this.apis.pubSub.publish(event, data, false);
    return promise;
  }
}

const generateSecondaryCurrentCartGetRequestKey = () => {
  return `${Date.now()}-${Math.random()}`;
};

const generateRequestKey = (prefix: string) => {
  return `${prefix}-${generateSecondaryCurrentCartGetRequestKey()}`;
};
